import { supabase } from '@/lib/supabase';
import { CACHE_KEYS, getCache, setCache } from '@/utils/cacheService';

// Thời gian cache
const CACHE_TTL = 5 * 60; // 5 phút

/**
 * Lấy danh sách các khóa học nổi bật (featured = true)
 */
export async function getFeaturedCourses(limit = 10) {
  try {
    // Kiểm tra cache
    const cacheKey = `featured_courses_${limit}`;
    const cachedData = getCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    const { data, error } = await supabase
      .from('courses')
      .select('*')
      .eq('featured', true)
      .eq('status', 'published')
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Error fetching featured courses:', error);
      throw new Error(error.message);
    }
    
    // Lưu vào cache
    setCache(cacheKey, data || [], CACHE_TTL);
    
    return data || [];
  } catch (error) {
    console.error('Failed to fetch featured courses:', error);
    throw error;
  }
}

/**
 * Lấy danh sách các sơ đồ nổi bật (featured = true)
 */
export async function getFeaturedDiagrams(limit = 10) {
  try {
    // Kiểm tra cache
    const cacheKey = `featured_diagrams_${limit}`;
    const cachedData = getCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }
    
    const { data, error } = await supabase
      .from('diagrams')
      .select('*')
      .eq('featured', true)
      .eq('status', 'published')
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Error fetching featured diagrams:', error);
      throw new Error(error.message);
    }
    
    // Lưu vào cache
    setCache(cacheKey, data || [], CACHE_TTL);
    
    return data || [];
  } catch (error) {
    console.error('Failed to fetch featured diagrams:', error);
    throw error;
  }
}

/**
 * Lấy danh sách tất cả khóa học
 */
export async function getAllCourses(options = {}) {
  const { 
    categoryId = null, 
    page = 1, 
    limit = 20, 
    searchQuery = null,
    sortBy = 'created_at',
    sortOrder = 'desc'
  } = options;
  
  try {
    let query = supabase
      .from('courses')
      .select('*, categories!inner(*)', { count: 'exact' })
      .eq('status', 'published');
    
    // Filter by category if provided
    if (categoryId) {
      query = query.eq('category_id', categoryId);
    }
    
    // Add search query if provided
    if (searchQuery) {
      query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
    }
    
    // Add sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });
    
    // Add pagination
    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);
    
    const { data, error, count } = await query;
    
    if (error) {
      console.error('Error fetching courses:', error);
      throw new Error(error.message);
    }
    
    return {
      courses: data || [],
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };
  } catch (error) {
    console.error('Failed to fetch courses:', error);
    throw error;
  }
}

/**
 * Lấy danh sách tất cả các sơ đồ
 */
export async function getAllDiagrams(options = {}) {
  const { 
    categoryId = null, 
    page = 1, 
    limit = 20, 
    searchQuery = null,
    sortBy = 'created_at',
    sortOrder = 'desc'
  } = options;
  
  try {
    let query = supabase
      .from('diagrams')
      .select('*, categories!inner(*)', { count: 'exact' })
      .eq('status', 'published');
    
    // Filter by category if provided
    if (categoryId) {
      query = query.eq('category_id', categoryId);
    }
    
    // Add search query if provided
    if (searchQuery) {
      query = query.or(`title.ilike.%${searchQuery}%,description.ilike.%${searchQuery}%`);
    }
    
    // Add sorting
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });
    
    // Add pagination
    const from = (page - 1) * limit;
    const to = from + limit - 1;
    query = query.range(from, to);
    
    const { data, error, count } = await query;
    
    if (error) {
      console.error('Error fetching diagrams:', error);
      throw new Error(error.message);
    }
    
    return {
      diagrams: data || [],
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit)
    };
  } catch (error) {
    console.error('Failed to fetch diagrams:', error);
    throw error;
  }
} 